import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { CreateVehicleOdometerTrackingDto } from '../../../vehicle-odometer-tracking';
import { VehicleRecordDto } from '../../dtos/vehicle-record.dto';
import { VehicleActionEnum } from '../../enums/vehicle-action.enum';
import { VehicleStatusEnum } from '../../enums/vehicle-status.enum';
import { updateVehicleOdometerSchema } from '../schemas/update-vehicle-odometer.schema';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: VehicleRecordDto | null,
	action: VehicleActionEnum,
	lastOdometerTracking?: CreateVehicleOdometerTrackingDto | null,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record && !lastOdometerTracking) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		VehicleStatusEnum.IN_PARK,
		VehicleStatusEnum.OUT_OF_SERVICE,
		VehicleStatusEnum.HARD_HOLD,
	];

	switch (action) {
		case VehicleActionEnum.PARK:
			validationResult = setStatusSchema(
				VehicleStatusEnum.IN_PARK,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleActionEnum.OUT_OF_SERVICE:
			validationResult = setStatusSchema(
				VehicleStatusEnum.OUT_OF_SERVICE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleActionEnum.HARD_HOLD:
			validationResult = setStatusSchema(
				VehicleStatusEnum.HARD_HOLD,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleActionEnum.SELL:
			validationResult = setStatusSchema(
				VehicleStatusEnum.SOLD,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case VehicleActionEnum.UPDATE_ODOMETER: {
			validationResult = updateVehicleOdometerSchema().validate(
				lastOdometerTracking,
				{
					abortEarly,
				},
			);
			break;
		}

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
